// =============================================================================
// upgrades.ts — Upgrades passivos oferecidos na Urna Eletrônica Hackeada.
// Só DADOS: os efeitos são aplicados no servidor (src/server/domain/upgrades.ts)
// pra manter o módulo compartilhado livre de lógica de simulação.
// As MAGNITUDES de cada efeito vivem em `fx` (fonte única da verdade): as
// fórmulas do servidor e as descrições abaixo leem os MESMOS números — mudou
// aqui, mudou na conta e no texto ao mesmo tempo, sem descolar.
// Descrições concretas (issue #31): número do efeito e quantas vezes acumula.
// Ícones são pixel art procedural do cliente (ui/pixelIcons.ts) — zero emoji.
// Na versão dourada "Emenda de Relator" (issue #93) o incremento é
// multiplicado por URNA_GOLDEN_MULT (~1,6x — ex.: +40% em vez de +25%).
// =============================================================================

export interface UpgradeDef {
  id: string;
  nome: string;
  desc: string;
  max: number;
  fx: Record<string, number>;   // magnitudes do efeito (só números, nunca funções)
  consumable?: boolean;   // não conta como passivo permanente (pode cair de novo)
}

// Formatação pt-BR das magnitudes dentro das descrições: mata o ruído de ponto
// flutuante (0.15*100 → "15") e troca ponto por vírgula decimal (0.5 → "0,5").
// Helpers puros de TEXTO — nada de lógica de simulação aqui.
export const fmtNum = (n: number): string => String(Math.round(n * 100) / 100).replace('.', ',');
export const fmtPct = (f: number): string => fmtNum(f * 100);

// -----------------------------------------------------------------------------
// Magnitudes — declaradas ANTES da tabela porque a desc interpola os mesmos
// números (propriedade não pode referenciar a irmã dentro do próprio literal).
// -----------------------------------------------------------------------------
// floor: piso de sanidade do cdMult (não aparece na desc)
const GABINETE_FX = { cdRedux: 0.22, floor: 0.05 };
// a vaga de aliado (+1) é estrutural/discreta: fica na prosa e no efeito
const ROBOS_FX = { allyDmg: 0.15 };
// intervalFloor é inalcançável no max atual (3), mas o servidor respeita
const FORO_FX = { baseInterval: 25, intervalStep: 3, intervalFloor: 15, firstDur: 2, durStep: 0.5 };
const PEC_FX = { dmg: 0.25 };
// gemFrac/spawnMult viram prosa ("metade"/"DOBRA"); os sistemas leem os números
const IMPEACH_FX = { gemFrac: 0.5, chaosDur: 20, spawnMult: 2 };
const AUXILIO_FX = { hp: 25 };
const ZE_FX = { regen: 1 };
const PASSAPORTE_FX = { speed: 0.12 };
const IMA_FX = { pickup: 0.4 };

export const UPGRADES = {
  gabinete_odio: {
    id: 'gabinete_odio',
    nome: 'Boca de Urna',
    desc: `Todas as suas armas recarregam ${fmtPct(GABINETE_FX.cdRedux)}% mais rápido. Panfletagem ilegal na porta da seção. Acumula até 5x.`,
    max: 5,
    fx: GABINETE_FX,
  },
  robos_whatsapp: {
    id: 'robos_whatsapp',
    nome: 'Robôs do WhatsApp',
    desc: `+1 vaga de aliado invocável e todos os seus aliados causam +${fmtPct(ROBOS_FX.allyDmg)}% de dano. Disparo em massa patrocinado. Acumula até 3x.`,
    max: 3,
    fx: ROBOS_FX,
  },
  foro_privilegiado: {
    id: 'foro_privilegiado',
    nome: 'Foro Privilegiado',
    desc: `A cada ${fmtNum(FORO_FX.baseInterval)}s você fica IMUNE por ${fmtNum(FORO_FX.firstDur)}s; cada nível extra encurta o ciclo em ${fmtNum(FORO_FX.intervalStep)}s e alonga a imunidade em ${fmtNum(FORO_FX.durStep)}s. A lei é igual pra todos*. (*exceto pra você) Acumula até 3x.`,
    max: 3,
    fx: FORO_FX,
  },
  pec_vinganca: {
    id: 'pec_vinganca',
    nome: 'Escândalo Político',
    desc: `+${fmtPct(PEC_FX.dmg)}% de dano em todas as armas. Manchete de domingo, dano de segunda a segunda. Acumula até 5x.`,
    max: 5,
    fx: PEC_FX,
  },
  impeachment_express: {
    id: 'impeachment_express',
    nome: 'Arquivamento de Processo',
    desc: `LIMPA a onda atual de inimigos (bosses e zumbis têm foro), e metade dos arquivados deixa votos no chão. Em troca, o caos institucional DOBRA o spawn por ${fmtNum(IMPEACH_FX.chaosDur)}s. Pedaladas incluídas.`,
    max: 99,           // consumível: pode cair de novo
    fx: IMPEACH_FX,
    consumable: true,
  },
  auxilio_brasil: {
    id: 'auxilio_brasil',
    nome: 'Auxílio Emergencial',
    desc: `+${fmtNum(AUXILIO_FX.hp)} de HP máximo e cura ${fmtNum(AUXILIO_FX.hp)} na hora. Coincidentemente perto da eleição. Acumula até 5x.`,
    max: 5,
    fx: AUXILIO_FX,
  },
  ze_gotinha: {
    id: 'ze_gotinha',
    nome: 'Vacina',
    desc: `Regenera +${fmtNum(ZE_FX.regen)} de HP por segundo. Ciência funcionando, apesar de tudo. Acumula até 3x.`,
    max: 3,
    fx: ZE_FX,
  },
  passaporte: {
    id: 'passaporte',
    nome: 'Viagem de Jatinho',
    desc: `+${fmtPct(PASSAPORTE_FX.speed)}% de velocidade de movimento. Ideal pra "férias" na Flórida quando o caldo entorna. Acumula até 3x.`,
    max: 3,
    fx: PASSAPORTE_FX,
  },
  ima_votos: {
    id: 'ima_votos',
    nome: 'Marketing Digital',
    desc: `+${fmtPct(IMA_FX.pickup)}% de raio de coleta de votos. O algoritmo entrega o voto na sua mão. Acumula até 3x.`,
    max: 3,
    fx: IMA_FX,
  },
} satisfies Record<string, UpgradeDef>;

export type UpgradeId = keyof typeof UPGRADES;

// Lookup por id vindo da rede (string qualquer)
export const UPGRADE_TABLE: Record<string, UpgradeDef | undefined> = UPGRADES;

export const UPGRADE_LIST: UpgradeDef[] = Object.values(UPGRADES);
